import { Modal } from '@/components/Layout';
import { ModalPositions } from '@/components/Layout/Modal/Modal.types';
import { FC, useEffect, useId } from 'react';
import {
  Button,
  FileUpload,
  MultiSelectDropDown,
  SelectDropDown,
  TextField,
} from '@/components/Common';
import { TextfieldType } from '@/components/Common/TextField/TextField.types';
import { Tabs, TextEditor } from '@/components/Ui';
import { FieldError } from 'react-hook-form';
import { LessonFormMode, useLessons } from './lessons.hook';
import { AlertModal } from '@/components/Layout/AlertModal/AlertModal';

interface LessonFormProps {
  mode: LessonFormMode;
}

const LessonForm: FC<LessonFormProps> = ({ mode }) => {
  const id = useId();
  const {
    isOpenLessonModal,
    isUpdatePending,
    isCreatePending,
    classes,
    series,
    activeTab,
    setActiveTab,
    handleChangeLecon,
    handleChangeResume,
    handleChangeClasses,
    handleChangeSeries,
    handleChangeDiscipline,
    cleanUp,
    onDraftLesson,
    onUpdateLesson,
    handleConfirmLessonAction,
    onCreateLesson,
    resumeHtmlText,
    leconHtmlText,
    register,
    registerUpdateForm,
    setValue,
    setUpdateValue,
    disciplines,
    errors,
    updateErrors,
    lesson,
    isModalConfirmationLessonOpen,
    setIsModalConfirmationLessonOpen,
    handleValidateAndPublish,
    setLeconHtmlText,
    setResumeHtmlText,
  } = useLessons();

  const isUpdateMode = mode === 'update';

  useEffect(() => {
    if (isUpdateMode && lesson) {
      setLeconHtmlText(lesson.longContent?.html || '');
      setResumeHtmlText(lesson.summary?.html || '');
      setUpdateValue('title', lesson.name);
      setUpdateValue('discipline', lesson.discipline?.name);
      setUpdateValue(
        'classe',
        lesson.classes?.map((cls) => ({ _id: cls._id, name: cls.name })) || [],
      );
      setUpdateValue(
        'serie',
        lesson.series?.map((sr) => ({ _id: sr._id, name: sr.name })) || [],
      );
    }
  }, [
    isUpdateMode,
    lesson,
    setLeconHtmlText,
    setResumeHtmlText,
    setUpdateValue,
  ]);

  const registerFn = isUpdateMode ? registerUpdateForm : register;
  const errorList = isUpdateMode ? updateErrors : errors;
  console.log(activeTab);
  return (
    <Modal
      isOpen={isOpenLessonModal}
      onClose={cleanUp}
      position={ModalPositions.RIGHT}
      modalStyle="p-0"
      headerStyle="bg-black"
    >
      <div className="h-[90vh] w-[70vw] bg-black relative">
        <form className={`grid grid-cols-[33%_auto] items-start`}>
          <div className="flex flex-col gap-4 h-[85vh] overflow-auto py-4 px-8">
            <h3 className="text-[30px] font-raleway font-bold leading-[49.32px] text-white text-center">
              {isUpdateMode ? 'Modifier leçon' : 'Configuration de la leçon'}
            </h3>
            <ul className="flex flex-col gap-8 pt-4">
              <li className="grid grid-cols-[15%_auto] gap-4">
                <div className="flex items-center justify-center h-[48px] w-[48px] rounded-full bg-[#1D1D1D]">
                  <i className="pi pi-file text-[#939393] text-xl"></i>
                </div>
                <TextField
                  inputStyle={{ background: '#1D1D1D' }}
                  placeholder={'Titre du chapitre'}
                  name="title"
                  register={registerFn('title')}
                  helpText={(errorList.title as FieldError)?.message}
                  errorStyle="text-red-500"
                  textFieldType={TextfieldType.INPUT}
                />
              </li>
              <li className="grid grid-cols-[15%_auto] gap-4">
                <div className="flex items-center justify-center h-[48px] w-[48px] rounded-full bg-[#1D1D1D]">
                  <i className="pi pi-graduation-cap text-[#939393] text-xl"></i>
                </div>
                <SelectDropDown
                  selectDropDownStyle={{
                    borderRadius: '0.375rem',
                    color: '#fff',
                    padding: ' 0.5rem',
                  }}
                  placeholder="Discpline"
                  name="discipline"
                  setValue={isUpdateMode ? setUpdateValue : setValue}
                  initialSelectedOptions={
                    isUpdateMode ? lesson?.discipline : null
                  }
                  helpText={(errorList.discipline as FieldError)?.message}
                  errorStyle="text-red-500"
                  options={disciplines || []}
                  handleChange={handleChangeDiscipline}
                />
              </li>

              <li className="grid grid-cols-[15%_auto] gap-4">
                <div className="flex items-center justify-center h-[48px] w-[48px] rounded-full bg-[#1D1D1D]">
                  <i className="pi pi-graduation-cap text-[#939393] text-xl"></i>
                </div>
                <MultiSelectDropDown
                  options={classes || []}
                  name="classe"
                  setValue={isUpdateMode ? setUpdateValue : setValue}
                  initialSelectedOptions={
                    isUpdateMode ? lesson?.classes || [] : undefined
                  }
                  handleChange={(selectedOptions) => {
                    const setValueFn = isUpdateMode ? setUpdateValue : setValue;
                    setValueFn('classe', selectedOptions);
                    setValueFn('classe', selectedOptions, {
                      shouldValidate: true,
                    });
                    handleChangeClasses(selectedOptions);
                  }}
                  helpText={(errorList.classe as FieldError)?.message}
                  errorStyle="text-red-500"
                  placeholder="Choix de la classe"
                />
              </li>
              <li className="grid grid-cols-[15%_auto] gap-4">
                <div className="flex items-center justify-center h-[48px] w-[48px] rounded-full bg-[#1D1D1D]">
                  <i className="pi pi-graduation-cap text-[#939393] text-xl"></i>
                </div>
                <MultiSelectDropDown
                  options={series ? series : []}
                  handleChange={(selectedOptions) => {
                    const setValueFn = isUpdateMode ? setUpdateValue : setValue;
                    setValueFn('serie', selectedOptions);
                    setValueFn('serie', selectedOptions, {
                      shouldValidate: true,
                    });
                    handleChangeSeries(selectedOptions);
                  }}
                  setValue={isUpdateMode ? setUpdateValue : setValue}
                  initialSelectedOptions={
                    isUpdateMode ? lesson?.series || [] : undefined
                  }
                  name="serie"
                  placeholder="Choix de la serie"
                  helpText={(errorList.serie as FieldError)?.message}
                  errorStyle="text-red-500"
                  multiSelectDropDownStyle="bg-[#1D1D1D]"
                />
              </li>

              <li className="grid grid-cols-[15%_auto] gap-4">
                <div className="flex items-center justify-center h-[48px] w-[48px] rounded-full bg-[#1D1D1D]">
                  <i className="pi pi-headphones text-[#939393] text-xl"></i>
                </div>
                <FileUpload
                  id={`${id}-audio`}
                  placeholder="Leçon audio"
                  name="audio"
                  disabled={false}
                  setValue={isUpdateMode ? setUpdateValue : setValue}
                  initialSelectedFile={
                    isUpdateMode ? lesson?.audioShortcut || '' : ''
                  }
                  helpText={(errorList.audio as FieldError)?.message}
                  errorStyle="text-red-500"
                  allowedFileTypes={['audio/mp3', 'audio/wav', 'audio/mpeg']}
                />
              </li>
              <li className="grid grid-cols-[15%_auto] gap-4">
                <div className="flex items-center justify-center h-[48px] w-[48px] rounded-full bg-[#1D1D1D]">
                  <i className="pi pi-image text-[#939393] text-xl"></i>
                </div>
                <FileUpload
                  id={`${id}-picture`}
                  placeholder="Image lecteur"
                  name="picture"
                  disabled={false}
                  setValue={isUpdateMode ? setUpdateValue : setValue}
                  initialSelectedFile={isUpdateMode ? lesson?.image || '' : ''}
                  helpText={(errors.picture as FieldError)?.message}
                  errorStyle="text-red-500"
                  allowedFileTypes={['image/jpeg', 'image/png']}
                />
              </li>
              <Button
                onClick={isUpdateMode ? onUpdateLesson : onDraftLesson}
                title={
                  isCreatePending || isUpdatePending
                    ? 'Enregistrer...'
                    : 'Enregistrer'
                }
                className="mt-2 bg-white text-black"
                type="button"
                disabled={isUpdatePending || isCreatePending}
              />
              <Button
                onClick={handleValidateAndPublish}
                title={
                  isUpdateMode && lesson?.isPublished ? 'Cacher' : 'Publier'
                }
                className="mt-1"
                type="button"
                disabled={isUpdatePending || isCreatePending}
              />
            </ul>
          </div>
          {/* Tab Section */}
          <div className="flex flex-col gap-6 p-4 h-[85vh] overflow-auto py-4 px-8">
            <Tabs
              activeIndex={activeTab}
              setActiveIndex={setActiveTab}
              tabs={[
                { id: 1, title: 'Leçon' },
                { id: 2, title: 'Résumé' },
              ]}
            />

            <TextEditor
              initialValue={
                isUpdateMode
                  ? activeTab === 1
                    ? lesson?.summary?.html || resumeHtmlText
                    : lesson?.longContent?.html || leconHtmlText
                  : ''
              }
              value={activeTab === 1 ? resumeHtmlText : leconHtmlText}
              backgroundColor="#000"
              onChange={
                activeTab === 1 ? handleChangeResume : handleChangeLecon
              }
              key={`editor-${activeTab}`}
            />

            <>
              {(errorList.longContent || errorList.summary) && (
                <div className="text-[12.8px] font-raleway pt-4 text-red-500">
                  {errorList.longContent?.html?.message && (
                    <div>{errorList.longContent.html.message}</div>
                  )}
                  {errorList.summary?.html?.message && (
                    <div>{errorList.summary.html.message}</div>
                  )}
                </div>
              )}
            </>
          </div>
          <AlertModal
            isOpen={isModalConfirmationLessonOpen}
            onClose={() => setIsModalConfirmationLessonOpen(false)}
            title={
              isUpdateMode && lesson?.isPublished
                ? 'Confirmation de cachage'
                : 'Confirmation de publication'
            }
            message={
              isUpdateMode && lesson?.isPublished
                ? 'Valider le cachage ?'
                : 'Valider la publication ?'
            }
            buttonTitle={
              isCreatePending || isUpdatePending ? 'En cours...' : 'Oui'
            }
            buttonCancelTitle="Non"
            handleConfirm={
              isUpdateMode ? handleConfirmLessonAction : onCreateLesson
            }
          />
        </form>
      </div>
    </Modal>
  );
};

export default LessonForm;
